import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDumbbell,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faCalendarDay,
  faClock,
  faNotesMedical,
  faArrowRight,
  faBaby,
  faFire,
  faCalendarWeek,
  faStopwatch,
  faBolt,
  faShieldHeart,
  faSpa,
  faChildReaching,
  faCalendarCheck,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import womensHealthService, {
  PostpartumProfile,
  PostpartumExercise,
} from '../../../services/womensHealthService';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import ProfileStrip from '../../../components/ProfileStrip/ProfileStrip';
import './PostpartumFitness.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type TabKey = 'workouts' | 'plans' | 'yoga' | 'babycare';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ChoiceOption {
  value: string;
  display_name: string;
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const todayStr = (): string => new Date().toISOString().split('T')[0];

const isThisWeek = (iso: string): boolean => {
  const d = new Date(iso);
  const now = new Date();
  const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  return d >= weekAgo && d <= now;
};

/* ================================================================== */
/*  Toast                                                              */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`wh-fit-toast wh-fit-toast-${type}`}>
      <span className="wh-fit-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="wh-fit-toast-message">{message}</span>
      <button className="wh-fit-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton                                                           */
/* ================================================================== */

const SkeletonCards: React.FC = () => (
  <div className="wh-fit-skeleton-grid">
    {[0, 1, 2].map((i) => (
      <div key={i} className="wh-fit-skeleton-card">
        <div className="wh-fit-skeleton-line long" />
        <div className="wh-fit-skeleton-line medium" />
        <div className="wh-fit-skeleton-line short" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Baby Care Links Data                                               */
/* ================================================================== */

const babyCareLinks = [
  {
    title: 'Child Development',
    description: 'Track your baby\'s developmental milestones, growth charts, and age-appropriate activities to support healthy growth.',
    icon: faChildReaching,
    href: '/child-development',
    color: '#3b82f6',
    lightColor: '#dbeafe',
  },
  {
    title: 'Feeding & Nutrition',
    description: 'Log breastfeeding sessions, track pumping volumes, and follow the solid food introduction timeline for your baby.',
    icon: faBaby,
    href: '/womens-health/feeding',
    color: '#22c55e',
    lightColor: '#dcfce7',
  },
  {
    title: 'My Bookings',
    description: 'Schedule and manage appointments with your healthcare provider for postpartum check-ups and baby wellness visits.',
    icon: faCalendarCheck,
    href: '/teleconsultation?tab=appointments',
    color: '#a855f7',
    lightColor: '#f3e8ff',
  },
];

/* ================================================================== */
/*  Initial Form                                                       */
/* ================================================================== */

const INITIAL_FORM = {
  exercise_type: '',
  date: todayStr(),
  duration_minutes: 15,
  completed: false,
  notes: '',
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const PostpartumFitness: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  // Data
  const [profile, setProfile] = useState<PostpartumProfile | null>(null);
  const [exercises, setExercises] = useState<PostpartumExercise[]>([]);
  const [exerciseChoices, setExerciseChoices] = useState<Record<string, ChoiceOption[]>>({});

  // UI
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'workouts');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  // Form
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Dynamic content from WHContent
  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Data Fetching                                                    */
  /* ---------------------------------------------------------------- */

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const pp = await womensHealthService.getActivePostpartum();
      setProfile(pp);

      if (pp) {
        const [exList, eChoices] = await Promise.all([
          womensHealthService.getPostpartumExercises(pp.id),
          womensHealthService.getPostpartumExerciseChoices(),
        ]);
        setExercises(Array.isArray(exList) ? exList : []);
        if (eChoices) setExerciseChoices(eChoices);
      }
    } catch (err) {
      console.error('Failed to fetch fitness data:', err);
      showToast('Failed to load fitness data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  /* ---------------------------------------------------------------- */
  /*  Stats                                                            */
  /* ---------------------------------------------------------------- */

  const stats = useMemo(() => {
    const totalWorkouts = exercises.filter((e) => e.completed).length;
    const totalMinutes = exercises.filter((e) => e.completed).reduce((sum, e) => sum + e.duration_minutes, 0);
    const thisWeekCount = exercises.filter((e) => e.completed && isThisWeek(e.date)).length;
    return { totalWorkouts, totalMinutes, thisWeekCount };
  }, [exercises]);

  /* ---------------------------------------------------------------- */
  /*  CRUD                                                             */
  /* ---------------------------------------------------------------- */

  const handleSave = async () => {
    if (!formData.exercise_type || !formData.date) {
      showToast('Please fill in exercise type and date.', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload = { ...formData, postpartum: profile!.id };
      if (editingId) {
        await womensHealthService.updatePostpartumExercise(editingId, payload);
        showToast('Exercise log updated!', 'success');
      } else {
        await womensHealthService.createPostpartumExercise(payload);
        showToast('Exercise logged!', 'success');
      }
      setFormData(INITIAL_FORM);
      setEditingId(null);
      setShowForm(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving exercise:', err);
      showToast('Failed to save exercise.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (ex: PostpartumExercise) => {
    setFormData({
      exercise_type: ex.exercise_type,
      date: ex.date,
      duration_minutes: ex.duration_minutes,
      completed: ex.completed,
      notes: ex.notes || '',
    });
    setEditingId(ex.id);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (confirmDelete === null) return;
    try {
      await womensHealthService.deletePostpartumExercise(confirmDelete);
      showToast('Exercise log deleted.', 'success');
      setConfirmDelete(null);
      await fetchData();
    } catch (err) {
      console.error('Error deleting exercise:', err);
      showToast('Failed to delete exercise.', 'error');
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Tabs                                                             */
  /* ---------------------------------------------------------------- */

  const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
    { key: 'workouts', label: t('womensHealth:postpartum.fitness.exercises'), icon: faDumbbell },
    { key: 'plans', label: t('womensHealth:postpartum.fitness.weeklyPlan'), icon: faFire },
    { key: 'yoga', label: t('womensHealth:postpartum.fitness.safeExercises'), icon: faSpa },
    { key: 'babycare', label: 'Baby Care', icon: faBaby },
  ];

  /* ================================================================ */
  /*  Render: No Profile                                               */
  /* ================================================================ */

  const renderNoProfile = () => (
    <div className="wh-fit-no-profile">
      <div className="wh-fit-no-profile-icon">
        <FontAwesomeIcon icon={faDumbbell} />
      </div>
      <h2>Set Up Your Postpartum Profile First</h2>
      <p>You need a postpartum profile to start tracking your fitness journey.</p>
      <a href="/womens-health/postpartum-recovery" className="wh-fit-btn wh-fit-btn-primary">
        <FontAwesomeIcon icon={faArrowRight} /> Go to Postpartum Recovery
      </a>
    </div>
  );

  /* ================================================================ */
  /*  Render: Stats Row                                                */
  /* ================================================================ */

  const renderStats = () => (
    <div className="wh-fit-stats-row">
      <div className="wh-fit-stat-card">
        <div className="wh-fit-stat-icon" style={{ background: '#f3e8ff', color: '#a855f7' }}>
          <FontAwesomeIcon icon={faDumbbell} />
        </div>
        <div className="wh-fit-stat-info">
          <span className="wh-fit-stat-value">{stats.totalWorkouts}</span>
          <span className="wh-fit-stat-label">Total Workouts</span>
        </div>
      </div>
      <div className="wh-fit-stat-card">
        <div className="wh-fit-stat-icon" style={{ background: '#fce7f3', color: '#ec4899' }}>
          <FontAwesomeIcon icon={faStopwatch} />
        </div>
        <div className="wh-fit-stat-info">
          <span className="wh-fit-stat-value">{stats.totalMinutes} min</span>
          <span className="wh-fit-stat-label">Total Minutes</span>
        </div>
      </div>
      <div className="wh-fit-stat-card">
        <div className="wh-fit-stat-icon" style={{ background: '#dbeafe', color: '#3b82f6' }}>
          <FontAwesomeIcon icon={faCalendarWeek} />
        </div>
        <div className="wh-fit-stat-info">
          <span className="wh-fit-stat-value">{stats.thisWeekCount}</span>
          <span className="wh-fit-stat-label">This Week</span>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Workouts Tab                                             */
  /* ================================================================ */

  const renderWorkoutsTab = () => (
    <div className="wh-fit-tab-panel">
      {/* Add / Edit Form */}
      {showForm ? (
        <div className="wh-fit-form-card">
          <h3 className="wh-fit-form-card-title">
            <FontAwesomeIcon icon={editingId ? faPen : faPlus} />
            {editingId ? 'Edit Exercise' : 'Log Exercise'}
          </h3>
          <div className="wh-fit-form-grid">
            <div className="wh-fit-form-group">
              <label className="wh-fit-form-label">Exercise Type <span className="wh-fit-required">*</span></label>
              <select
                className="wh-fit-form-select"
                value={formData.exercise_type}
                onChange={(e) => setFormData({ ...formData, exercise_type: e.target.value })}
              >
                <option value="">Select...</option>
                {(exerciseChoices.exercise_type || []).map((c) => (
                  <option key={c.value} value={c.value}>{c.display_name}</option>
                ))}
              </select>
            </div>
            <div className="wh-fit-form-group">
              <label className="wh-fit-form-label">Date <span className="wh-fit-required">*</span></label>
              <input
                type="date"
                className="wh-fit-form-input"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>
            <div className="wh-fit-form-group">
              <label className="wh-fit-form-label">Duration (minutes)</label>
              <input
                type="number"
                className="wh-fit-form-input"
                min={1}
                max={180}
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 15 })}
              />
            </div>
            <div className="wh-fit-form-group wh-fit-checkbox-group">
              <label className="wh-fit-checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.completed}
                  onChange={(e) => setFormData({ ...formData, completed: e.target.checked })}
                />
                <span className="wh-fit-checkbox-custom">
                  {formData.completed && <FontAwesomeIcon icon={faCheck} />}
                </span>
                Completed
              </label>
            </div>
          </div>
          <div className="wh-fit-form-group" style={{ marginTop: 12 }}>
            <label className="wh-fit-form-label">Notes</label>
            <textarea
              className="wh-fit-form-textarea"
              rows={3}
              placeholder="How did the workout go..."
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            />
          </div>
          <div className="wh-fit-form-actions">
            <button
              className="wh-fit-btn wh-fit-btn-cancel"
              onClick={() => {
                setShowForm(false);
                setEditingId(null);
                setFormData(INITIAL_FORM);
              }}
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              className="wh-fit-btn wh-fit-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> {t('common:buttons.save')}...</>
              ) : (
                <><FontAwesomeIcon icon={faCheck} /> {editingId ? t('common:buttons.update') : t('common:buttons.save')}</>
              )}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="wh-fit-btn wh-fit-btn-primary wh-fit-add-btn"
          onClick={() => {
            setFormData(INITIAL_FORM);
            setEditingId(null);
            setShowForm(true);
          }}
        >
          <FontAwesomeIcon icon={faPlus} /> Log Exercise
        </button>
      )}

      {/* Exercise List */}
      {loading ? (
        <SkeletonCards />
      ) : exercises.length === 0 ? (
        <div className="wh-fit-empty-state">
          <div className="wh-fit-empty-icon-wrap">
            <FontAwesomeIcon icon={faDumbbell} />
          </div>
          <h3>No workouts logged yet</h3>
          <p>Start tracking your postpartum fitness journey by logging your first exercise.</p>
        </div>
      ) : (
        <div className="wh-fit-cards-grid">
          {exercises.map((ex) => (
            <div key={ex.id} className="wh-fit-entry-card">
              <div className="wh-fit-entry-header">
                <span className="wh-fit-entry-date">
                  <FontAwesomeIcon icon={faCalendarDay} /> {formatDate(ex.date)}
                </span>
                <span className={`wh-fit-badge ${ex.completed ? 'wh-fit-badge-completed' : 'wh-fit-badge-pending'}`}>
                  <FontAwesomeIcon icon={ex.completed ? faCheck : faClock} />
                  {ex.completed ? 'Completed' : 'Pending'}
                </span>
              </div>
              <div className="wh-fit-badges-row">
                <span className="wh-fit-badge wh-fit-badge-type">
                  <FontAwesomeIcon icon={faDumbbell} /> {ex.exercise_type_display || ex.exercise_type}
                </span>
                <span className="wh-fit-badge wh-fit-badge-duration">
                  <FontAwesomeIcon icon={faClock} /> {ex.duration_minutes} min
                </span>
              </div>
              {ex.notes && (
                <p className="wh-fit-entry-notes">
                  <FontAwesomeIcon icon={faNotesMedical} /> {ex.notes}
                </p>
              )}
              <div className="wh-fit-entry-actions">
                <button className="wh-fit-action-btn wh-fit-action-edit" onClick={() => handleEdit(ex)}>
                  <FontAwesomeIcon icon={faPen} /> {t('common:buttons.edit')}
                </button>
                <button className="wh-fit-action-btn wh-fit-action-delete" onClick={() => setConfirmDelete(ex.id)}>
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Render: Fitness Plans Tab                                        */
  /* ================================================================ */

  const renderPlansTab = () => (
    <div className="wh-fit-tab-panel">
      <div className="wh-fit-guide-intro">
        <h3>Postpartum Fitness Recovery Plan</h3>
        <p>
          A structured approach to rebuilding your fitness after delivery. Each phase builds on the
          previous one. Always listen to your body and consult your healthcare provider before starting
          or advancing to the next phase. Your recovery timeline may differ based on your delivery type
          and individual health factors.
        </p>
      </div>

      <WHArticleList
        stage="postpartum"
        contentType="fitness_phases"
        variant="accordion"
        icon={faDumbbell}
        accentColor="#3b82f6"
      />
    </div>
  );

  /* ================================================================ */
  /*  Render: Yoga & Core Tab                                          */
  /* ================================================================ */

  const renderYogaTab = () => (
    <div className="wh-fit-tab-panel">
      <div className="wh-fit-section">
        <h3 className="wh-fit-section-title">
          <FontAwesomeIcon icon={faSpa} /> Postpartum Yoga Poses
        </h3>
        <WHArticleList
          stage="postpartum"
          contentType="yoga_poses"
          variant="card"
          icon={faSpa}
          accentColor="#10b981"
        />
      </div>

      <div className="wh-fit-section">
        <h3 className="wh-fit-section-title">
          <FontAwesomeIcon icon={faShieldHeart} /> Diastasis Recti
        </h3>
        <WHArticleList
          stage="postpartum"
          contentType="diastasis_recti"
          variant="accordion"
          icon={faShieldHeart}
          accentColor="#ec4899"
        />
      </div>

      <div className="wh-fit-section">
        <h3 className="wh-fit-section-title">
          <FontAwesomeIcon icon={faBolt} /> Core Rehabilitation Exercises
        </h3>
        <WHArticleList
          stage="postpartum"
          contentType="core_strengthening"
          variant="card"
          icon={faBolt}
          accentColor="#f59e0b"
        />
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Baby Care Tab                                            */
  /* ================================================================ */

  const renderBabyCareTab = () => (
    <div className="wh-fit-tab-panel">
      <div className="wh-fit-guide-intro">
        <h3>Baby Care Quick Links</h3>
        <p>
          Access essential baby care resources all in one place. Track development milestones,
          manage feeding schedules, and book healthcare appointments for you and your baby.
        </p>
      </div>

      <div className="wh-fit-babycare-grid">
        {babyCareLinks.map((link, idx) => (
          <a
            key={idx}
            href={link.href}
            className="wh-fit-babycare-card"
            style={{ borderTopColor: link.color }}
          >
            <div className="wh-fit-babycare-icon" style={{ background: link.lightColor, color: link.color }}>
              <FontAwesomeIcon icon={link.icon} />
            </div>
            <div className="wh-fit-babycare-info">
              <h4>{link.title}</h4>
              <p>{link.description}</p>
            </div>
            <div className="wh-fit-babycare-arrow" style={{ color: link.color }}>
              <FontAwesomeIcon icon={faArrowRight} />
            </div>
          </a>
        ))}
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="wh-fit-page">
      {/* Toast */}
      {toast && (
        <div className="wh-fit-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Modal */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Delete Exercise?"
        message="This action cannot be undone. Are you sure?"
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setConfirmDelete(null)}
      />

      {/* Hero Banner */}
      <section className="wh-fit-hero">
        <div className="wh-fit-hero-circle wh-fit-hero-circle-1" />
        <div className="wh-fit-hero-circle wh-fit-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="wh-fit-hero-content">
            <div className="wh-fit-hero-badge">
              <FontAwesomeIcon icon={faDumbbell} />
              Postpartum
            </div>
            <h1 className="wh-fit-hero-title">{t('womensHealth:postpartum.fitness.title')}</h1>
            <p className="wh-fit-hero-subtitle">
              Track workouts, follow phased fitness plans, practice postpartum yoga, and access baby care resources.
            </p>
          </div>
        </Container>
      </section>

      {/* Stats */}
      {profile && (
        <section className="wh-fit-stats-section">
          <Container>
            {renderStats()}
          </Container>
        </section>
      )}

      {/* Content */}
      <Container>
        {loading ? (
          <SkeletonCards />
        ) : !profile ? (
          renderNoProfile()
        ) : (
          <>
            <ProfileStrip
              summary={`Delivery ${formatDate(profile.delivery_date)} · ${profile.recovery_mode_display || profile.recovery_mode || 'Recovery'}`}
              hubPath="/womens-health/postpartum-recovery"
              hubLabel="Recovery Hub"
            />
            <div className="wh-fit-tabs-container">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  className={`wh-fit-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <FontAwesomeIcon icon={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="wh-fit-tab-content">
              {activeTab === 'workouts' && renderWorkoutsTab()}
              {activeTab === 'plans' && renderPlansTab()}
              {activeTab === 'yoga' && renderYogaTab()}
              {activeTab === 'babycare' && renderBabyCareTab()}
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default PostpartumFitness;
